import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  runInInjectionContext,
  signal,
  type SimpleChanges,
  untracked,
  viewChild,
} from '@angular/core';

import { HrAssistantComponent } from '@core/intelligence-smart-ui';
import {
  TellentAppShellStateService,
  TellentMainNavigationStateService,
} from '@core/tellent-logic';
import { TellentNavigationViewID } from '@core/tellent-types';
import { IconsContainerComponent } from '@core/user-interface-legacy';
import { WalkthroughStepId } from '@core/walkthrough-types';

import { TELLENT_APP_SHELL_LAYOUT } from './tellent-app-shell-constants';
import { TellentAppSwitcherNextComponent } from '../tellent-app-switcher-next/tellent-app-switcher-next.component';
import { TellentMainNavigationComponent } from '../tellent-navigation/main-navigation/main-navigation.component';
import { TellentSettingsNavigationComponent } from '../tellent-navigation/settings-navigation/settings-navigation.component';

@Component({
  selector: 'tlc-app-shell',
  styleUrls: [
    './tellent-app-shell.component.desktop.less',
    './tellent-app-shell.component.mobile.less',
    './tellent-app-shell.component.less',
  ],
  templateUrl: './tellent-app-shell.component.html',
  imports: [
    IconsContainerComponent,
    TellentMainNavigationComponent,
    TellentSettingsNavigationComponent,
    TellentAppSwitcherNextComponent,
    NgTemplateOutlet,
    HrAssistantComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.device--desktop]': '!shellState.isMobile()',
    '[class.device--mobile]': 'shellState.isMobile()',
    '[class.layout--minimal]': 'shellState.isMinimalLayout()',
    '[class.view--settings]': 'shellState.view() === TellentNavigationID.Settings',
    '[class.view--main]': 'shellState.view() === TellentNavigationID.Main',
  },
  viewProviders: [TellentAppShellStateService],
})
export class TellentAppShellComponent {
  #injector = inject(Injector);

  public readonly TELLENT_APP_SHELL_LAYOUT = TELLENT_APP_SHELL_LAYOUT;
  public readonly TellentNavigationID = TellentNavigationViewID;

  public shellState = inject(TellentAppShellStateService);
  public mainNavState = inject(TellentMainNavigationStateService);

  public isMobile = input.required<boolean>();
  public isMinimalLayout = input(false);
  public shouldExpandNavOnHover = input(true);

  public isSettingsView = computed(
    () => this.shellState.view() === TellentNavigationViewID.Settings,
  );

  public componentsWalkthroughId = input<
    Partial<{
      appSwitcher: WalkthroughStepId;
      navigation: WalkthroughStepId;
    }>
  >({});

  public mainNav = viewChild(TellentMainNavigationComponent);

  public isSidebarAvailable = computed(() => {
    const forceMaximized = this.mainNavState.forceMaximized();
    const isMobile = this.shellState.isMobile();
    const isMinimalLayout = this.shellState.isMinimalLayout();

    return !isMinimalLayout ? (isMobile && forceMaximized) || !isMobile : false;
  });

  public switcherTriggerWidth = signal<number>(0);
  public topbarStartLabelOffset = computed(() => {
    const minimized = this.mainNavState.minimized();
    const switcherTriggerOffset = this.switcherTriggerWidth();
    const forceMaximized = this.mainNavState.forceMaximized();

    if (minimized) return switcherTriggerOffset;
    if (forceMaximized) return TELLENT_APP_SHELL_LAYOUT.sidebarExpandedWidth - 20; // substracting topbar separator margin

    return 0;
  });

  constructor() {
    effect(() => {
      this.shellState.setIsMobileContext(this.isMobile());
      this.shellState.setIsMinimalLayout(this.isMinimalLayout());
    });

    effect(() => {
      const minimized = this.mainNavState.minimized();
      const switcherTriggerOffset = untracked(() => this.switcherTriggerWidth());

      if (minimized && !switcherTriggerOffset) {
        this.measureAndUptadeMinimizedAppSwitcherTriggerWidth();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isMobile) this.shellState.setIsMobileContext(this.isMobile());
    if (changes.isMinimalLayout) this.shellState.setIsMinimalLayout(this.isMinimalLayout());
  }

  private measureAndUptadeMinimizedAppSwitcherTriggerWidth() {
    runInInjectionContext(this.#injector, () => {
      const ref = afterNextRender(() => {
        const width = this.mainNav()?.appSwitcher()?.triggerWidth();
        this.switcherTriggerWidth.set(width);
        ref.destroy();
      });
    });
  }
}
